/**
 * /data-export — explains how data export works in detail. Direct
 * answer to "what happens if I want to leave / migrate elsewhere?"
 * High-trust signal for B2B.
 */
import { MarketingShell } from '../MarketingShell';

export function DataExport() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/data-export"
      documentTitle="Data export at Draftlize · Draftlize"
      documentDescription="Every Draftlize artifact (cards, threads, documents, history, refs) exports to plain markdown on demand. No lock-in, no proprietary format. Take it to Obsidian, Notion, vim, anywhere."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Data export</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Your data, <em>portable.</em>
          </h1>
          <p className="lede">
            Plain answer: every artifact you create in Draftlize exports to plain markdown on demand. No lock-in. No proprietary format. Take it to Obsidian, Notion, vim, a folder on your laptop — wherever.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">What's <em>exported.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Cards</h3>
            <p>Every card → one <code>.md</code> file with YAML frontmatter (id, kind, pack, author, created_at) + body text + wikilink edges in line. Same format as the CLI's <code>draftlize pull</code> output.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Threads</h3>
            <p>Each thread → one markdown file with the full conversation history in chronological order. Tool calls inline as code blocks. Compaction state preserved as metadata.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Documents</h3>
            <p>The long-form artifacts you generated (design-doc, pitch-deck, blog-post, outline) as their final markdown, with the <code>source_snapshot</code> showing which cards they were derived from.</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">Card history</h3>
            <p>Every edit, every promote, every delete preserved with timestamp + author + source message. The full audit trail.</p>
          </div>
          <div className="problem-col">
            <span className="num">V</span>
            <h3 className="serif">Refs</h3>
            <p>Every file you uploaded to <code>_refs/</code> downloaded as-is. Original filenames, original content.</p>
          </div>
          <div className="problem-col">
            <span className="num">VI</span>
            <h3 className="serif">Memory + frames + edges</h3>
            <p>The project <code>memory/PROJECT.md</code>, all frame views, all card-to-card edges. The complete graph state.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">What it <em>looks like.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">A single .zip per project</h3>
            <p>Triggered from Settings → Export. Background job assembles the zip, emails you a download link when ready (usually &lt;60 seconds for a typical project).</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Directory layout</h3>
            <p><code>cards/c-04.md</code>, <code>threads/&lt;thread-id&gt;.md</code>, <code>documents/&lt;doc-id&gt;.md</code>, <code>card_history/&lt;card-id&gt;.json</code>, <code>refs/&lt;filename&gt;</code>, <code>memory/PROJECT.md</code>, <code>frames/&lt;frame-id&gt;.json</code>, <code>edges.json</code>.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Round-tripping</h3>
            <p>An exported zip can be imported back into Draftlize (or another tenant). No data loss in the round-trip: ids, edges, history, all preserved. We test this in CI.</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">If you <em>cancel.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">30 days read-only</h3>
            <p>Your account stays accessible read-only for 30 days after cancellation. You can still browse cards, export, copy text. You can't create new threads or call the LLM.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Final reminder email</h3>
            <p>Day 25 we email you "your data will be deleted in 5 days, export now if you haven't." Day 30 we delete.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Deletion is permanent</h3>
            <p>We don't keep "soft deleted" backups around for future revenue extraction. Within 7 days of deletion, the data is gone from all live DBs and backup cycles.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Draftlize is paid software. We earn renewal by being useful, not by holding your data hostage when you try to leave.
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start free with <em>$5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            The export endpoint works from day one. Sign up, use your free $5 to build a project, try the export to confirm the format — top up only if and when you want more.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
